import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Scale } from "lucide-react";

/**
 * Window Tint visualizer — studio close-up of the side glass with
 * pre-rendered film darkness per level (pixel-exact edges, instant switch).
 */

const LEVELS = [
  {
    id: "50",
    name: "50% Light",
    tag: "Subtle",
    blurb: "Barely-there smoke that kills glare and UV without changing the look.",
    points: ["Keeps the cabin bright", "Blocks harmful UV", "Great for a factory-plus feel"],
  },
  {
    id: "35",
    name: "35% Medium",
    tag: "Balanced",
    blurb: "The sweet spot — visible shade, real privacy, still easy to see out at night.",
    points: ["Noticeable privacy", "Strong heat rejection", "Night visibility stays good"],
  },
  {
    id: "20",
    name: "20% Dark",
    tag: "Most Popular",
    blurb: "The enthusiast favourite. Deep shade, serious privacy, aggressive stance.",
    points: ["Hard to see inside", "Maximum heat rejection", "Sharp, sporty look"],
  },
  {
    id: "5",
    name: "5% Limo",
    tag: "Maximum",
    blurb: "Near-black privacy glass. Total seclusion for the rear cabin.",
    points: ["Almost zero visibility in", "Full privacy for passengers & gear", "Boldest look possible"],
  },
];

const TINT_IMG = {
  "50": "/ppf/tint-50.png?v=2",
  "35": "/ppf/tint-35.png?v=2",
  "20": "/ppf/tint-20.png?v=2",
  "5": "/ppf/tint-5.png?v=2",
};

export default function TintVisualizer({ onSelect }) {
  const [active, setActive] = useState("20");
  const lvl = LEVELS.find((l) => l.id === active);

  useEffect(() => {
    Object.values(TINT_IMG).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="tint-visualizer">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Shade Selector</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white mb-4">
          Pick your <span className="text-white/50">shade.</span>
        </h2>
        <p className="text-white/50 max-w-xl text-sm md:text-base mb-12">
          The number is visible light transmission — lower % means darker film.
        </p>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Car visual — pre-rendered glass darkness per level */}
          <div className="relative rounded-3xl border border-white/10 bg-[#0b0b0e] overflow-hidden" data-testid="tint-car">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <AnimatePresence mode="wait">
              <motion.img
                key={lvl.id}
                src={TINT_IMG[lvl.id]}
                alt={`${lvl.name} window tint preview on vehicle side glass`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="relative z-10 w-full h-auto block"
                draggable={false}
              />
            </AnimatePresence>
            <div className="absolute top-4 right-5 z-20 px-3 py-1.5 rounded-full bg-black/60 border border-white/15 backdrop-blur font-mono-alt text-[11px] text-white tracking-widest" data-testid="tint-level-badge">
              VLT {lvl.id}%
            </div>
            <div className="relative z-10 px-6 pb-5 pt-3 flex items-center gap-2 text-[11px] text-white/45 border-t border-white/5">
              <span className="inline-block w-3 h-3 rounded-sm bg-[#05050c] border border-white/20" />
              live shade preview — legal placement notes below
            </div>
          </div>

          {/* Level selector */}
          <div>
            <div className="grid grid-cols-2 gap-2 mb-8" role="tablist">
              {LEVELS.map((l) => (
                <button
                  key={l.id}
                  role="tab"
                  aria-selected={active === l.id}
                  data-testid={`tint-level-${l.id}`}
                  onClick={() => { setActive(l.id); onSelect?.(`${l.id}% Tint`); }}
                  className={`px-4 py-3.5 rounded-xl border text-left transition-all duration-300 ${
                    active === l.id
                      ? "bg-[#1F5EFF]/15 border-[#1F5EFF] text-white"
                      : "bg-white/5 border-white/10 text-white/60 hover:text-white hover:border-white/25"
                  }`}
                >
                  <div className="font-mono-alt text-[9px] uppercase tracking-widest text-[#8db2ff]">{l.tag}</div>
                  <div className="font-display text-base md:text-lg mt-0.5">{l.name}</div>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={lvl.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
              >
                <p className="text-white/60 text-sm leading-relaxed mb-5">{lvl.blurb}</p>
                <div className="space-y-2 mb-6" data-testid="tint-level-points">
                  {lvl.points.map((p) => (
                    <div key={p} className="flex items-center gap-3 text-sm text-white/80">
                      <span className="w-5 h-5 rounded-md bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#8db2ff]" strokeWidth={3} />
                      </span>
                      {p}
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-8" data-testid="tint-legal-note">
              <Scale className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-white/55 leading-relaxed">
                Alberta law: front side windows and windshield must stay clear of dark film —
                these shades go on rear side & rear glass. We'll set you up fully legal.
              </p>
            </div>

            <a
              href="#ppf-waitlist"
              data-testid="tint-level-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-amber-400 text-black font-semibold hover:bg-white transition-colors"
            >
              Join Waitlist — {lvl.name}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
